/**
 * API client for Natalon backend
 */

import axios, { type AxiosInstance } from 'axios';
import type {
  LocationSearchResult,
  ChartCalculationRequest,
  ChartCalculationResponse,
  UserChartsResponse,
  ChartSaveRequest,
  ChartSaveResponse,
  UserSettingsResponse,
  UserSettingsUpdateRequest,
  TimezonesResponse,
  FamilyResponse,
  FamilyRole,
  ToggleForecastResponse,
  FamilyForecastResponse,
  TopicsResponse,
} from '../types';

// Get API base URL from environment variable (set at build time)
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

class NatalonApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Set auth header immediately if available (before any API calls)
    // window.Telegram.WebApp.initData is available synchronously from telegram-web-app.js
    if (typeof window !== 'undefined' && window.Telegram?.WebApp?.initData) {
      this.client.defaults.headers.common['X-Telegram-Init-Data'] = window.Telegram.WebApp.initData;
    }

    // Add response interceptor to handle errors
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('[API Client] Request failed:', error.response?.status);
        return Promise.reject(error);
      }
    );
  }

  /**
   * Set Telegram init data for authentication
   */
  setInitData(initData: string): void {
    this.client.defaults.headers.common['X-Telegram-Init-Data'] = initData;
  }

  /**
   * Search for locations by query
   */
  async searchLocations(query: string, limit: number = 5): Promise<LocationSearchResult[]> {
    const response = await this.client.get<LocationSearchResult[]>('/locations/search', {
      params: { query, limit },
    });
    return response.data;
  }

  /**
   * Calculate natal chart
   */
  async calculateChart(request: ChartCalculationRequest): Promise<ChartCalculationResponse> {
    const response = await this.client.post<ChartCalculationResponse>('/charts/calculate', request);
    return response.data;
  }

  /**
   * Get user's saved charts
   */
  async getUserCharts(): Promise<UserChartsResponse> {
    const response = await this.client.get<UserChartsResponse>('/charts');
    return response.data;
  }

  /**
   * Get full chart by ID
   */
  async getChartById(chartId: string): Promise<ChartCalculationResponse> {
    const response = await this.client.get<ChartCalculationResponse>(`/charts/${chartId}`);
    return response.data;
  }

  /**
   * Save calculated chart to database
   */
  async saveChart(request: ChartSaveRequest): Promise<ChartSaveResponse> {
    const response = await this.client.post<ChartSaveResponse>('/charts/save', request);
    return response.data;
  }

  /**
   * Delete a chart by ID
   */
  async deleteChart(chartId: string): Promise<{ success: boolean; error?: string }> {
    const response = await this.client.delete<{ success: boolean; error?: string }>(`/charts/${chartId}`);
    return response.data;
  }

  /**
   * Get user settings
   */
  async getSettings(): Promise<UserSettingsResponse> {
    const response = await this.client.get<UserSettingsResponse>('/settings');
    return response.data;
  }

  /**
   * Update user settings
   */
  async updateSettings(request: UserSettingsUpdateRequest): Promise<UserSettingsResponse> {
    const response = await this.client.put<UserSettingsResponse>('/settings', request);
    return response.data;
  }

  /**
   * Get available timezones
   */
  async getTimezones(): Promise<TimezonesResponse> {
    const response = await this.client.get<TimezonesResponse>('/settings/timezones');
    return response.data;
  }

  // --- Family API ---

  /**
   * Get user's family group
   */
  async getFamilyGroup(): Promise<FamilyResponse> {
    const response = await this.client.get<FamilyResponse>('/family');
    return response.data;
  }

  /**
   * Create a family group
   */
  async createFamily(name: string): Promise<FamilyResponse> {
    const response = await this.client.post<FamilyResponse>('/family', { name });
    return response.data;
  }

  /**
   * Delete a family group
   */
  async deleteFamily(groupId: number): Promise<FamilyResponse> {
    const response = await this.client.delete<FamilyResponse>(`/family/${groupId}`);
    return response.data;
  }

  /**
   * Add a member to family
   */
  async addFamilyMember(
    groupId: number,
    chartId: string,
    role: FamilyRole,
    displayName?: string
  ): Promise<FamilyResponse> {
    const response = await this.client.post<FamilyResponse>(`/family/${groupId}/members`, {
      chartId,
      role,
      displayName,
    });
    return response.data;
  }

  /**
   * Remove a member from family
   */
  async removeFamilyMember(groupId: number, memberId: number): Promise<FamilyResponse> {
    const response = await this.client.delete<FamilyResponse>(`/family/${groupId}/members/${memberId}`);
    return response.data;
  }

  /**
   * Toggle family forecast on/off
   */
  async toggleFamilyForecast(groupId: number): Promise<ToggleForecastResponse> {
    const response = await this.client.put<ToggleForecastResponse>(`/family/${groupId}/forecast`);
    return response.data;
  }

  /**
   * Get instant family forecast
   */
  async getFamilyForecast(groupId: number): Promise<FamilyForecastResponse> {
    const response = await this.client.post<FamilyForecastResponse>(`/family/${groupId}/forecast/now`);
    return response.data;
  }

  /**
   * Get available interpretation topics
   */
  async getTopics(): Promise<TopicsResponse> {
    const response = await this.client.get<TopicsResponse>('/topics');
    return response.data;
  }

  /**
   * Stream interpretation for a topic via SSE.
   * Yields text chunks. Throws on HTTP errors (402 = paywall, 429 = rate limit).
   * Returns { done: true } when complete, or { error: string } on generation failure.
   */
  async *streamInterpretation(
    chartId: string,
    topic: string
  ): AsyncGenerator<
    { text?: string; done?: boolean; error?: string; reset?: boolean },
    void,
    unknown
  > {
    const response = await fetch(`${API_BASE_URL}/interpretations/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Telegram-Init-Data': this.client.defaults.headers.common['X-Telegram-Init-Data'] as string || '',
      },
      body: JSON.stringify({ chartId, topic }),
    });

    if (!response.ok) {
      const error = new Error(`HTTP error! status: ${response.status}`) as Error & { status: number };
      error.status = response.status;
      throw error;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Response body is not readable');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      // Keep last incomplete line in buffer
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            yield data;
          } catch {
            // skip malformed JSON
          }
        }
      }
    }

    // Process remaining buffer
    if (buffer.startsWith('data: ')) {
      try {
        const data = JSON.parse(buffer.slice(6));
        yield data;
      } catch {
        // skip
      }
    }
  }
}

// Export singleton instance
export const api = new NatalonApiClient();

export default api;
